import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ExtractedEntity } from '@/lib/pdf/entityExtractor'

// What user already knows (from intake)
export interface IntakeAnswers {
  hasExistingBusiness: boolean
  customerInterviews: number
  hasPitchDeck: boolean
  knownCompetitors: string[]
  hasValidatedPricing: boolean
  isGreenfield: boolean
  ideaSentence: string
}

// AI-generated suggestions from idea sentence
export interface AIAutoFill {
  segment: string
  job: string
  pains: string[]
  competitors: string[]
  productType: string
}

// Gate progress tracking
export interface GateProgress {
  who: number // 0-100
  whatProblem: number
  whyMe: number
  willItMakeMoney: number
}

interface DiscoveryState {
  // Intake flow state
  hasCompletedIntake: boolean
  intakeAnswers: IntakeAnswers | null
  aiAutoFill: AIAutoFill | null
  extractedEntities: ExtractedEntity[]

  // Gate progress
  gateProgress: GateProgress

  // Actions
  setIntakeComplete: (answers: IntakeAnswers) => void
  setAIAutoFill: (autoFill: AIAutoFill) => void
  setExtractedEntities: (entities: ExtractedEntity[]) => void
  resetIntake: () => void
  updateGateProgress: (gate: keyof GateProgress, progress: number) => void

  // Computed: overall PMF confidence
  getPMFConfidence: () => number
}

// Default intake values (exported for potential use in tests/reset)
export const DEFAULT_INTAKE: IntakeAnswers = {
  hasExistingBusiness: false,
  customerInterviews: 0,
  hasPitchDeck: false,
  knownCompetitors: [],
  hasValidatedPricing: false,
  isGreenfield: true,
  ideaSentence: '',
}

const DEFAULT_GATE_PROGRESS: GateProgress = {
  who: 0,
  whatProblem: 0,
  whyMe: 0,
  willItMakeMoney: 0,
}

export const useDiscoveryStore = create<DiscoveryState>()(
  persist(
    (set, get) => ({
      hasCompletedIntake: false,
      intakeAnswers: null,
      aiAutoFill: null,
      extractedEntities: [],
      gateProgress: DEFAULT_GATE_PROGRESS,

      setIntakeComplete: (answers) => {
        set({
          hasCompletedIntake: true,
          intakeAnswers: answers,
        })
      },

      setAIAutoFill: (autoFill) => {
        set({ aiAutoFill: autoFill })
      },

      setExtractedEntities: (entities) => {
        set({ extractedEntities: entities })
      },

      resetIntake: () => {
        set({
          hasCompletedIntake: false,
          intakeAnswers: null,
          aiAutoFill: null,
          extractedEntities: [],
          gateProgress: DEFAULT_GATE_PROGRESS,
        })
      },

      updateGateProgress: (gate, progress) => {
        set((state) => ({
          gateProgress: {
            ...state.gateProgress,
            [gate]: Math.min(100, Math.max(0, progress)),
          },
        }))
      },

      getPMFConfidence: () => {
        const { gateProgress } = get()
        const weights = { who: 0.25, whatProblem: 0.30, whyMe: 0.20, willItMakeMoney: 0.25 }
        return Math.round(
          gateProgress.who * weights.who +
          gateProgress.whatProblem * weights.whatProblem +
          gateProgress.whyMe * weights.whyMe +
          gateProgress.willItMakeMoney * weights.willItMakeMoney
        )
      },
    }),
    {
      name: 'intellispot-discovery',
      partialize: (state) => ({
        hasCompletedIntake: state.hasCompletedIntake,
        intakeAnswers: state.intakeAnswers,
        aiAutoFill: state.aiAutoFill,
        extractedEntities: state.extractedEntities,
        gateProgress: state.gateProgress,
      }),
    }
  )
)
